import { useState, useEffect, useRef } from "react";

import { withTranslation } from "react-i18next";
import { Box, FormControl, TextField, Typography, Select, MenuItem, InputLabel, FormControlLabel, Checkbox } from "@mui/material";
import AppCard from "@plannel/components/ui/AppCard";
import CardContent from "@mui/material/CardContent";
import Snackbar from "@plannel/components/Snackbar";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { isEmpty, isEqual, isNull, size, includes } from "lodash";
import debug from "@plannel/utils/debug-util";

import planVersionService from "@plannel/services/data/plan-version-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import measureService from "@plannel/services/data/measure-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import Dialog from "@plannel/components/Dialog";
import dateUtils from "@plannel/utils/date-util";
import codeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import ComputeMeasureModal from "../dp-workbench/dp-workbench-modal/ComputeMeasureModal";
import { CalculationButton, DetailButton } from "@plannel/components/ActionIconButton";
import WorkbenchUtil from "../dp-workbench/WorkbenchUtil";
import DpInitialSettingModal from "./DpInitialSettingModal";

const DpCreateScenarios = props => {
  const { t, close, open, userRoles, running } = props;
  const { dpInitalValues, roles, module, dpConfig, dpVersion, dpSettingCode } = codeUtil;
  const moduleCd = module.DP;
  const { INITIAL_DEMAND, AVG_FLG, AVG_MONTHS, POS_ACTUAL_FLG, POS_AVG_FLG, POS_AVG_MONTHS } = dpConfig;
  const { SCENARIO, RELEASED, RUNNING } = dpVersion;
  const { snackBarMessageColor } = ColorUtil;
  const { LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, MEASURE_CUSTOM, CUSTOMIZATION, DP_VERSION } = dpInitalValues;
  const { BASELINE_FORECAST, ITEM_LEVEL_ID, CUST_LEVEL_ID, ITEM_LEVEL, CUST_LEVEL, ROLLING_RANGE } = dpSettingCode;
  const { BF_QTY } = codeUtil.dpMeasure;

  const CUSTOMER = codeUtil.customer;
  const ITEM = codeUtil.item;
  // Measure Customization 의 compute setting 안에 Baseline Forecast(BF_QTY)를 source로 쓰는 항목이 있으면
  // BASELINE_FORECAST 레벨 선택이 필요하다
  const hasBaselineForecastSource = list => (list ?? []).some(compute => Object.values(compute).some(expr => expr?.includes(`[${BF_QTY.cd}]`)));

  const [versionList, setVersionList] = useState([]);
  const [activeVersionList, setActiveVersionList] = useState([]);
  const [selectVersionId, setSelectVersionId] = useState("");
  const [scenarioNm, setScenarioNm] = useState("");
  const [scenarioDesc, setScenarioDesc] = useState("");
  const [mainVersion, setMainVersion] = useState({}); //scenario && parent == null

  const [initialMeasureList, setInitialMeasureList] = useState([]);
  const [initialTarget, setInitialTarget] = useState([]);
  const [computeMeasureList, setComputeMeasureList] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [initialSettingOpen, setInitialSettingOpen] = useState(false);

  const [oriMeasureList, setOriMeasureList] = useState([]);
  const [initialValue, setInitialValue] = useState(NOTINITIAL);
  const [customInitialData, setCustomInitialData] = useState({});
  const [itemLevelCds, setItemLevelCds] = useState([]);
  const [custLevelCds, setCustLevelCds] = useState([]);
  const [itemLevelCd, setItemLevelCd] = useState([]);
  const [custLevelCd, setCustLevelCd] = useState([]);
  const [rollingRange, setRollingRange] = useState(0);
  const [avgFlg, setAvgFlg] = useState(false);
  const [avgMonths, setAvgMonths] = useState(3);
  const [posAvgFlg, setPosAvgFlg] = useState(false);
  const [posAvgMonths, setPosAvgMonths] = useState(3);
  const [posActualFlg, setPosActualFlg] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [isError, setIsError] = useState(false);
  const [isLvError, setIsLvError] = useState(false);

  const demandValList = [LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, CUSTOMIZATION];
  // [다음 릴리즈 배포] Measure Customization — 이번 릴리즈 제외
  // const demandValList = [LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, MEASURE_CUSTOM, CUSTOMIZATION];
  const initialValues = includes(userRoles, roles.APP_BF) ? [DP_VERSION, BASELINE_FORECAST, ...demandValList] : [DP_VERSION, ...demandValList];

  const scenarioNameList = useRef([]);

  const createVersionName = () => {
    planVersionService
      .createVersionName({ moduleCd })
      .then(response => {
        setScenarioNm(response.data);
      })
      .catch(e => {
        debug(e);
        setScenarioNm(`${moduleCd}${dateUtils.getToday()}S001`);
      });
  };

  const retrievePlanVersion = () => {
    planVersionService
      .getAll({ moduleCd, statusCds: [SCENARIO, RELEASED], pagination: false, orderByColumn: "releaseTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        const nameList = [];
        if (!isEmpty(results)) {
          setVersionList(results);
          for (const result of results) {
            if (result?.parent === null && result?.statusCd === SCENARIO) {
              setMainVersion(result);
            }
            nameList.push(result.name);
          }
          scenarioNameList.current = nameList;
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveHrchyConfig = () => {
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const itemConfig = [];
        const custConfig = [];
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a, b) => {
            if (a.seqNum < b.seqNum) {
              return -1;
            }

            if (a.seqNum > b.seqNum) {
              return 1;
            }
            return 0;
          });
          for (const row of result) {
            if (row.itemHrchyFlg) {
              itemConfig.push(row);
            } else {
              custConfig.push(row);
            }
          }
          setItemLevelCds([...itemConfig, ITEM]);
          setCustLevelCds([...custConfig, CUSTOMER]);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveMeasures = () => {
    measureService
      .getAll({ isBasic: true })
      .then(response => {
        if (response.data?.results) {
          const result = response.data.results;
          setOriMeasureList(result);
          // v26.10: POS 미출시 — SHOW_POS 가 꺼져 있으면 POS_QTY 를 target 으로 내놓지 않는다
          setInitialTarget(result.filter(item => item.measureCd.includes("SF") || item.measureCd.includes("MF")
            || (codeUtil.SHOW_POS && item.measureCd.includes("POS_QTY"))));
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const handleChange = event => {
    if (event.target.name === "initialValue") {
      setInitialValue(event.target.value);
      const defaultComputeList = WorkbenchUtil.getDefaultComputeList(event.target.value);
      setComputeMeasureList(defaultComputeList);
      if (!isEmpty(versionList)) {
        let filterData = [];
        if (event.target.value === LAST_RELEASED_VERSION) {
          filterData = versionList?.filter(x => x.statusCd === "RELEASED");
        } else if (event.target.value === DP_VERSION) {
          filterData = versionList?.filter(x => x.statusCd === "SCENARIO");
        } else if (event.target.value === BASELINE_FORECAST) {
          filterData = versionList?.filter(x => x.statusCd === "SCENARIO" && x.parent === null);
        }
        if (!isEmpty(filterData)) {
          setActiveVersionList(filterData);
          setSelectVersionId(filterData[0].id);
        } else {
          if (event.target.value !== LAST_RELEASED_VERSION) {
            setActiveVersionList(versionList);
            setSelectVersionId(versionList[0].id);
          } else {
            setActiveVersionList([]);
            setSelectVersionId("");
            setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoReleasedVersion") });
          }
        }
      }
      if (event.target.value === BASELINE_FORECAST) {
        setCustLevelCd(custLevelCds[0] ?? CUSTOMER)
        setItemLevelCd(itemLevelCds[0] ?? ITEM)
      }
    } else if (event.target.name === CUST_LEVEL) {
      const cust = custLevelCds.filter(function (el) {
        return el.id === event.target.value;
      })[0];
      setCustLevelCd(cust);
    } else if (event.target.name === ITEM_LEVEL) {
      const item =
        itemLevelCds.filter(function (el) {
          return el.id === event.target.value;
        })[0] ?? ITEM;
      setItemLevelCd(item);
    } else if (event.target.name === "avgFlg") {
      setAvgFlg(event.target.checked);
    } else if (event.target.name === "posAvgFlg") {
      setPosAvgFlg(event.target.checked);
    }
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };

  const clickInitialDetail = params => {
    if (params.type === "click") {
      setInitialSettingOpen(true);
    }
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };
  
  const handelIntialSettingClose = () => {
    setInitialSettingOpen(false);
  };

  const handleClose = () => {
    close(false);
  };

  const handleCreate = () => {
    if (!scenarioNm) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_InputMandatory") });
      setIsError(true);
      return false;
    }

    if (initialValue === CUSTOMIZATION) {
      if (isEmpty(customInitialData?.objVal)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoDataByCause", { cause: t(CUSTOMIZATION), title: t("scenario") }) });
        setIsError(true);
        return false;
      }
    }

    // CUSTOMIZATION 이나 NOTINITIAL 이 아닌데 computeMeasureList 가 비어있으면 생성 불가
    if ((initialValue !== CUSTOMIZATION && initialValue !== NOTINITIAL)  && size(computeMeasureList) < 1) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ComputeSettingRequired") });
      return false;
    }

    if (scenarioNameList.current.includes(scenarioNm)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ScenarioExist") });
      setIsError(true);
      return false;
    }

    const selectedVersion = versionList.filter(version => isEqual(selectVersionId, version.id));
    if (isEmpty(selectedVersion)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoScenario") });
      setIsError(true);
      return false;
    }
    const { moduleCd: versionModuleCd } = selectedVersion[0];
    let parentVersion = selectedVersion[0].parent;
    let planConfigObj = selectedVersion[0].planConfigObj;
    if (!isEmpty(mainVersion) && initialValue === LAST_RELEASED_VERSION) {
      planConfigObj = mainVersion.planConfigObj;
      parentVersion = mainVersion;
    }
    planConfigObj[INITIAL_DEMAND] = { [`${initialValue}`]: computeMeasureList, [ROLLING_RANGE]: rollingRange };
    const needsLevel = initialValue === BASELINE_FORECAST || (initialValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList));
    if (needsLevel) {
      if (isEmpty(custLevelCd) || isEmpty(itemLevelCd)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ConfirmLevel") });
        setIsLvError(true);
        return false;
      }
      planConfigObj[INITIAL_DEMAND] = { ...planConfigObj[INITIAL_DEMAND], [CUST_LEVEL_ID]: custLevelCd ? custLevelCd.id : custLevelCds[0].id, [ITEM_LEVEL_ID]: itemLevelCd ? itemLevelCd.id : itemLevelCds[0].id };
    } else if (initialValue === CUSTOMIZATION) {
      if (!isEmpty(mainVersion) && customInitialData?.objVal?.defaultValue === LAST_RELEASED_VERSION) {
        planConfigObj = mainVersion.planConfigObj;
        parentVersion = mainVersion;
      }
      planConfigObj[INITIAL_DEMAND] = customInitialData?.objVal;
    }
    planConfigObj[AVG_FLG] = avgFlg;
    planConfigObj[AVG_MONTHS] = avgMonths;
    planConfigObj[POS_AVG_FLG] = posAvgFlg;
    planConfigObj[POS_AVG_MONTHS] = posAvgMonths;
    planConfigObj[POS_ACTUAL_FLG] = posActualFlg;

    delete planConfigObj.BF_SETTING;

    const params = {
      parent: isNull(parentVersion) ? selectedVersion[0] : parentVersion,
      planConfigObj,
      name: scenarioNm,
      moduleCd: versionModuleCd,
      descTxt: scenarioDesc,
      statusCd: RUNNING,
      copyTarget: selectedVersion[0],
    };

    planVersionService
      .createScenario(params)
      .then(response => {
        const { planVersion } = response.data;
        if (planVersion) {
          if (response.data.message) {
            const { message } = response.data;
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_CreateVersion") });
          }
        } else {
          if (response.data.message) {
            const { message } = response.data;
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          }
        }
        running(false);
      })
      .catch(e => {
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.saveFail,
          content: e.response?.data ? t(e.response.data) : t("MSG_CreateVersionFailed"),
        });
        debug(e);
        running(false);
      });
    handleClose();
    running(true);
    return true;
  };

  const settingPlanConfigs = (result) => {
    if (result?.strVal) {
      setInitialValue(result.strVal);
    }
    if (isEqual(result?.strVal, CUSTOMIZATION)) {
      setCustomInitialData(result);
    }

    if (result.objVal && Object.keys(result.objVal).length > 0) {
      for (const key of Object.keys(result.objVal)) {
        if (key === CUST_LEVEL_ID) {
          const cust = custLevelCds.filter(function (el) {
            return el.id === result.objVal[key];
          })[0];
          setCustLevelCd(cust);
        } else if (key === ITEM_LEVEL_ID) {
          const item = itemLevelCds.filter(function (el) {
            return el.id === result.objVal[key];
          })[0];
          setItemLevelCd(item);
        } else if (key === ROLLING_RANGE) {
          setRollingRange(result.objVal[key]);
        } else {
          if (key !== "defaultValue" && key !== CUSTOMIZATION && key !== "copyTarget") {
            setComputeMeasureList(result.objVal[key]);
          }
        }
      }
    } else {
      const defaultComputeList = WorkbenchUtil.getDefaultComputeList(result.strVal);
      setComputeMeasureList(defaultComputeList);
    }
  };

  const initialPlanConfig = () => {
    const param = {
      moduleCd,
      activeFlg: true,
    };

    planConfigService.getAll(param)
      .then(response => {
        if (response.data?.results) {
          const resultData = response.data.results;
          for (const result of resultData) {
            if (isEqual(result.configCd, INITIAL_DEMAND)) {
              setCustomInitialData(result);
            } else if (isEqual(result.configCd, AVG_FLG)) {
              setAvgFlg(result.boolVal != null && result.boolVal === true);
              setAvgMonths(result.numVal ?? 3);
              setPosAvgFlg(result.objVal?.POS_AVG_FLG ?? false);
            } else if (isEqual(result.configCd, POS_ACTUAL_FLG)) {
              setPosAvgMonths(result.numVal ?? 3);
              setPosActualFlg(result.boolVal);
            }
          }
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        retrievePlanVersion();
      });
  };

  const handleDialog = action => {
    if (action === "CREATE" || action === "WARNING") {
      handleCreate();
    }
  };

  useEffect(() => {
    if (open) {
      retrieveHrchyConfig();
      createVersionName();
      retrieveMeasures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!isEmpty(itemLevelCds)) {
      initialPlanConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemLevelCds]);

  useEffect(() => {
    if (scenarioNm) {
      setIsError(false);
    }
  }, [scenarioNm]);

  useEffect(() => {
    if (oriMeasureList.length > 0) {
     const initialMeasures = codeUtil.getSourceMeasureList(oriMeasureList, initialValue, posActualFlg);
     setInitialMeasureList(initialMeasures);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, posActualFlg]);

  useEffect(() => {
    if (!isEmpty(versionList)) {
      let filterData = [];
      if (initialValue === LAST_RELEASED_VERSION) {
        filterData = versionList?.filter(x => x.statusCd === "RELEASED");
      } else if (initialValue === DP_VERSION) {
        filterData = versionList?.filter(x => x.statusCd === "SCENARIO");
      } else if (initialValue === BASELINE_FORECAST) {
        filterData = versionList?.filter(x => x.statusCd === "SCENARIO" && x.parent === null);
      }
      if (!isEmpty(filterData)) {
        setActiveVersionList(filterData);
        setSelectVersionId(filterData[0].id);
      } else {
        if (initialValue !== LAST_RELEASED_VERSION) {
          setActiveVersionList(versionList);
          setSelectVersionId(versionList[0].id);
        } else {
          setActiveVersionList([]);
          setSelectVersionId("");
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoReleasedVersion") });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionList]);

  useEffect(() => {
    if (!isEmpty(customInitialData)) {
      settingPlanConfigs(customInitialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customInitialData]);

  // Measure Customization 의 compute setting에 Baseline Forecast 를 source로 쓰는 항목이 생기면
  // BASELINE_FORECAST 와 마찬가지로 레벨 선택이 필요해지므로 기본값을 채워 준다.
  useEffect(() => {
    if (initialValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList)) {
      setCustLevelCd(prev => (isEmpty(prev) ? (custLevelCds[0] ?? CUSTOMER) : prev));
      setItemLevelCd(prev => (isEmpty(prev) ? (itemLevelCds[0] ?? ITEM) : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, computeMeasureList]);

  return (
    <>
      {/* 아이콘은 PlayArrow — 저장이 아니라 생성이다. DP Process Mgmt 의 "Start DP" 버튼과 맞춘 것.
          주의: 이 프로젝트의 RunIcon 도 PlayArrow 라 Run 모달의 실행 버튼과 같은 아이콘이 된다. */}
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("createScenario")}
        width={420}
        saveLabel={t("create")}
        saveIcon={<PlayArrowIcon />}
        onSave={() => {
          if ((initialValue !== CUSTOMIZATION && initialValue !== NOTINITIAL) && size(computeMeasureList) < 1) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ComputeSettingRequired") });
            return;
          }
          setDialogInfo({ open: true, title: "create", content: t("MSG_CreateVersionConfirm") });
        }}>
          <AppCard
            variant="outlined"
            sx={{ mb: 1 }}>
            <CardContent sx={{ pb: "20px!important" }}>
              <Typography
                id="modal-modal-title"
                variant="body1"
                sx={{ mb: 2, ml: 1 }}>
                {t("baseForecastVersion")}
              </Typography>
              <Box
                size="small"
                sx={{ mt: 1, gap: 1, display: "flex", textAlign: "center" }}>
                <FormControl
                  size="small"
                  sx={{ width: "90%" }}>
                  <Select
                    size="small"
                    name="versionId"
                    variant="outlined"
                    value={selectVersionId}
                    disabled={initialValue !== DP_VERSION}
                    onChange={event => {
                      setSelectVersionId(event.target.value);
                    }}>
                    {activeVersionList?.map(version => {
                      return (
                        <MenuItem
                          value={version.id}
                          key={version.id}>
                          {version.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
              <WorkbenchUtil.MeasureFormulaBox
                computeMeasureList={computeMeasureList}
                oriMeasureList={oriMeasureList}
              />
              <Box
                size="small"
                sx={{ mt: 2 }}>
                <FormControl
                  size="small"
                  sx={{ width: "90%" }}>
                  <InputLabel>{t("createDefaultValue")}</InputLabel>
                  <Select
                    error={isError}
                    size="small"
                    variant="outlined"
                    name="initialValue"
                    label={t("createDefaultValue")}
                    value={initialValue}
                    onChange={event => {
                      handleChange(event);
                    }}>
                    {initialValues?.map(initalValue => {
                      return (
                        <MenuItem
                          key={initalValue}
                          value={initalValue}>
                          {t(initalValue)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {initialValue !== CUSTOMIZATION ? (
                  <CalculationButton
                    sx={{ width: "10%" }}
                    label="computeExpressSetting"
                    color={computeMeasureList.length > 0 ? "primary" : "black"}
                    onClick={filterActionItem}
                    disabled={initialValue === NOTINITIAL}
                  />
                ) : null}
                {initialValue === CUSTOMIZATION ? (
                  <DetailButton
                    sx={{ width: "10%" }}
                    label="initialPlanDetailSetting"
                    color={"primary"}
                    onClick={clickInitialDetail}
                  />
                  ) : null}
              </Box>
              {initialValue === BASELINE_FORECAST || (initialValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList)) ? (
                <Box sx={{ mt: 1 }}>
                  <FormControl
                    size="small"
                    sx={{ width: "44%", mr: "1%" }}>
                    <InputLabel>{t(CUST_LEVEL)}</InputLabel>
                    <Select
                      variant="outlined"
                      id={CUST_LEVEL}
                      name={CUST_LEVEL}
                      value={!isEmpty(custLevelCd) ? custLevelCd?.id : custLevelCds ? custLevelCds[0]?.id : CUSTOMER.id}
                      label={t(CUST_LEVEL)}
                      error={isLvError}
                      onChange={event => {
                        handleChange(event);
                      }}>
                      {custLevelCds?.map(custLevel => {
                        return (
                          <MenuItem
                            key={custLevel.id}
                            value={custLevel.id}
                          >
                            {custLevel.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl
                    size="small"
                    sx={{ width: "45%" }}>
                    <InputLabel>{t(ITEM_LEVEL)}</InputLabel>
                    <Select
                      variant="outlined"
                      id={ITEM_LEVEL}
                      name={ITEM_LEVEL}
                      value={!isEmpty(itemLevelCd) ? itemLevelCd?.id : itemLevelCds ? itemLevelCds[0]?.id : ITEM.id}
                      label={t(ITEM_LEVEL)}
                      error={isLvError}
                      onChange={event => {
                        handleChange(event);
                      }}>
                      {itemLevelCds?.map(itemLevel => {
                        return (
                          <MenuItem
                            key={itemLevel.id}
                            value={itemLevel.id}
                          >
                            {itemLevel.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              ) : (
                <Box></Box>
              )}
              <Box>
                <Box sx={{mt: 1}}>
                  <FormControlLabel
                    control={<Checkbox color="primary" name={"avgFlg"} checked={avgFlg}
                                       onChange={param => handleChange(param)}/>}
                    label={t("avgNoOfMonths", { N: avgMonths, measureName: t("avgSalesActual") })} key="AVG_FLG" sx={{display: "flex"}}
                  />
                </Box>
                {codeUtil.SHOW_POS && (
                <Box sx={{mt: 1}}>
                  <FormControlLabel
                    control={<Checkbox color="primary" name={"posAvgFlg"} checked={posAvgFlg}
                                       onChange={param => handleChange(param)}/>}
                    label={t("avgNoOfMonths", { N: posAvgMonths, measureName: t("posAvgSalesActual") })} key="POS_AVG_FLG" sx={{display: "flex"}}
                  />
                </Box>
                )}
              </Box>
            </CardContent>
          </AppCard>
          <AppCard variant="outlined">
            <CardContent sx={{ pb: 0 }}>
              <Box
                size="small"
                sx={{ gap: 1 }}>
                <Typography
                  id="modal-modal-title"
                  variant="body1"
                  sx={{ mb: 2, ml: 1 }}>
                  {t("newForecastScenario")}
                </Typography>
                <FormControl
                  size="small"
                  style={{ width: "100%" }}>
                  <TextField
                    label={t("scenarioName")}
                    size="small"
                    value={scenarioNm}
                    type="string"
                    onChange={e => {
                      setScenarioNm(e.target.value);
                    }}
                    error={isError}
                  />
                </FormControl>
              </Box>
              <Box
                size="small"
                sx={{ mt: 2, gap: 1 }}>
                <FormControl
                  size="small"
                  style={{ width: "100%" }}>
                  <TextField
                    label={t("forecastStartTime")}
                    size="small"
                    value={dateUtils.formatDateTime(new ZDate())}
                    type="string"
                    readOnly
                    disabled
                  />
                </FormControl>
              </Box>
              <Box
                size="small"
                sx={{ mt: 2, gap: 1 }}>
                <FormControl
                  size="small"
                  style={{ width: "100%" }}>
                  <TextField
                    label={t("descTxt")}
                    size="small"
                    value={scenarioDesc}
                    type="string"
                    onChange={e => {
                      setScenarioDesc(e.target.value);
                    }}
                    error={isError}
                  />
                </FormControl>
              </Box>
            </CardContent>
          </AppCard>
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <ComputeMeasureModal
        open={filterModalOpen}
        close={handelFilterClose}
        modalResultData={setComputeMeasureList}
        target={initialTarget.length > 0 ? initialTarget : oriMeasureList}
        source={initialMeasureList.length > 0 ? initialMeasureList : oriMeasureList}
        computeList={computeMeasureList}
      />
      <DpInitialSettingModal
        open={initialSettingOpen}
        close={handelIntialSettingClose}
        itemLevelCds={itemLevelCds}
        custLevelCds={custLevelCds}
        initialValues={initialValues}
        oriMeasureList={oriMeasureList}
        configDatas={customInitialData}
        modalResultData={setCustomInitialData}
        versionList={versionList}
        setVersionId={setSelectVersionId}
        posActualFlg={posActualFlg}
      />
    </>
  );
};

export default withTranslation()(DpCreateScenarios);
